import React, { useEffect, useMemo, useState } from 'react';
import Shell from '../../components/Shell';
import api from '../../services/api';

type Doc = {
  slug: string;
  title: string;
  pack: string;
  sort: number;
  pdfReady: boolean;
  stale: boolean;
};

type OfferingGroup = {
  id: string;
  lane: string;
  title: string;
  subtitle?: string;
  status: 'active' | 'planned';
  name: string;
  sourceDir: string;
  documentCount: number;
  pdfReadyCount: number;
  staleCount: number;
  documents: Doc[];
};

type Lane = {
  id: string;
  label: string;
  description: string;
};

type Status = {
  lanes: Lane[];
  groups: OfferingGroup[];
  documentCount: number;
  pdfReadyCount: number;
  staleCount: number;
};

const PACK_LABELS: Record<string, string> = {
  'first-close': 'First close ($20K)',
  'every-close': 'Every close',
  'additional-close': 'Additional close ($40–80K)',
  contribution: 'Contribution close (assets, no extra shares)',
};

const PACK_ORDER = [
  'first-close',
  'every-close',
  'additional-close',
  'contribution',
];

const GROUP_HELP: Record<string, string> = {
  'consular-equity-series-2026':
    'Form Inc. first. First close: Disclosure → Questionnaire → SPA → SHA → Wire to Inc. + bank PDF. Additional: Joinder → SHA → Wire to Inc. Contribution pack is internal (IP / Eagle / Dirt Path / Equilibria 40%).',
};

export default function AdminLegalBridgePage() {
  const [status, setStatus] = useState<Status | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    const { data } = await api.get('/admin/legal-bridge');
    setStatus(data);
    setSelectedGroupId((prev) => {
      if (prev && data.groups.some((g: OfferingGroup) => g.id === prev)) {
        return prev;
      }
      const firstActive = data.groups.find(
        (g: OfferingGroup) => g.status === 'active',
      );
      return firstActive?.id ?? data.groups[0]?.id ?? null;
    });
  }

  useEffect(() => {
    load().catch(() => setMsg('Failed to load legal documents'));
  }, []);

  const selectedGroup = useMemo(
    () => status?.groups.find((g) => g.id === selectedGroupId) ?? null,
    [status, selectedGroupId],
  );

  const lanesWithGroups = useMemo(() => {
    if (!status) return [];
    return status.lanes
      .map((lane) => ({
        lane,
        groups: status.groups.filter((g) => g.lane === lane.id),
      }))
      .filter((row) => row.groups.length > 0);
  }, [status]);

  const packGroups = useMemo(() => {
    if (!selectedGroup) return [];
    return PACK_ORDER.map((pack) => ({
      pack,
      label: PACK_LABELS[pack] || pack,
      docs: selectedGroup.documents.filter((d) => d.pack === pack),
    })).filter((g) => g.docs.length > 0);
  }, [selectedGroup]);

  async function regenerate(groupId: string, slug?: string) {
    setBusy(true);
    setMsg('');
    try {
      const url = slug
        ? `/admin/legal-bridge/groups/${encodeURIComponent(groupId)}/regenerate?slug=${encodeURIComponent(slug)}`
        : `/admin/legal-bridge/groups/${encodeURIComponent(groupId)}/regenerate`;
      const { data } = await api.post(url);
      setStatus(data);
      const failed = data.results?.filter((r: { ok: boolean }) => !r.ok) ?? [];
      if (failed.length) {
        setMsg(
          `Some PDFs failed: ${failed.map((f: { slug: string }) => f.slug).join(', ')}`,
        );
      } else {
        setMsg(
          slug ? `Regenerated ${slug}.` : `Regenerated all PDFs in ${groupId}.`,
        );
      }
    } catch (err: any) {
      setMsg(err?.response?.data?.message || 'Regeneration failed');
    } finally {
      setBusy(false);
    }
  }

  async function downloadPdf(groupId: string, slug: string) {
    const token = localStorage.getItem('authToken');
    const res = await fetch(
      `/api/v1/admin/legal-bridge/groups/${encodeURIComponent(groupId)}/documents/${encodeURIComponent(slug)}/pdf?disposition=attachment`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) {
      alert('PDF unavailable — regenerate first');
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Shell title="Admin · Legal docs" wide>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Legal document PDFs</h1>
          <p className="mt-2 max-w-2xl text-sm text-ink-700">
            Markdown under <code className="text-xs">legal/</code> is the single
            source of truth. PDFs are admin-only — not on the investor portal.
            Attach bank wire instructions separately when emailing.
          </p>
        </div>
        {selectedGroup?.status === 'active' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => regenerate(selectedGroup.id)}
            className="bg-navy-900 px-4 py-2 text-sm font-semibold text-mist-50 hover:bg-navy-700 disabled:opacity-50"
          >
            {busy ? 'Generating…' : `Regenerate ${selectedGroup.title}`}
          </button>
        )}
      </div>

      {msg && <p className="mt-3 text-sm text-ink-700">{msg}</p>}

      {status && (
        <p className="mt-4 text-xs text-ink-700">
          {status.pdfReadyCount}/{status.documentCount} PDFs ready across all
          offerings
          {status.staleCount > 0 && (
            <span className="text-amber-800"> · {status.staleCount} stale</span>
          )}
        </p>
      )}

      <div className="mt-8 grid gap-8 lg:grid-cols-[240px_1fr]">
        <aside className="space-y-8">
          {lanesWithGroups.map(({ lane, groups }) => (
            <div key={lane.id}>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-navy-700">
                {lane.label}
              </p>
              <p className="mt-1 text-xs text-ink-700">{lane.description}</p>
              <ul className="mt-3 space-y-1">
                {groups.map((group) => {
                  const active = group.id === selectedGroupId;
                  return (
                    <li key={group.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedGroupId(group.id)}
                        className={`w-full border px-3 py-2.5 text-left text-sm transition ${
                          active
                            ? 'border-navy-700 bg-navy-900 text-mist-50'
                            : 'border-mist-200 bg-white text-ink-900 hover:border-navy-400'
                        }`}
                      >
                        <span className="font-medium">{group.title}</span>
                        {group.status === 'planned' && (
                          <span
                            className={`ml-2 text-xs ${active ? 'text-mist-200' : 'text-ink-700'}`}
                          >
                            planned
                          </span>
                        )}
                        {group.status === 'active' && (
                          <span
                            className={`mt-0.5 block text-xs ${active ? 'text-mist-200' : 'text-ink-700'}`}
                          >
                            {group.pdfReadyCount}/{group.documentCount} PDFs
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </aside>

        <div>
          {!selectedGroup && (
            <p className="text-sm text-ink-700">Select an offering.</p>
          )}

          {selectedGroup?.status === 'planned' && (
            <div className="border border-mist-200 bg-white px-5 py-8">
              <h2 className="font-display text-xl">{selectedGroup.title}</h2>
              {selectedGroup.subtitle && (
                <p className="mt-2 text-sm text-ink-700">
                  {selectedGroup.subtitle}
                </p>
              )}
              <p className="mt-4 text-sm text-ink-700">
                Planned — add markdown and catalog entries under{' '}
                <code className="text-xs">{selectedGroup.sourceDir}/</code> when
                counsel-ready.
              </p>
            </div>
          )}

          {selectedGroup?.status === 'active' && (
            <>
              <div className="border border-mist-200 bg-white px-5 py-4">
                <h2 className="font-display text-xl">{selectedGroup.title}</h2>
                {selectedGroup.subtitle && (
                  <p className="mt-1 text-sm text-ink-700">
                    {selectedGroup.subtitle}
                  </p>
                )}
                <p className="mt-2 text-xs text-ink-700">
                  Source:{' '}
                  <code className="text-xs">{selectedGroup.sourceDir}/</code>
                  {' · '}
                  {selectedGroup.pdfReadyCount}/{selectedGroup.documentCount}{' '}
                  PDFs ready
                  {selectedGroup.staleCount > 0 && (
                    <span className="text-amber-800">
                      {' '}
                      · {selectedGroup.staleCount} stale
                    </span>
                  )}
                </p>
                {GROUP_HELP[selectedGroup.id] && (
                  <p className="mt-3 text-sm text-ink-700">
                    {GROUP_HELP[selectedGroup.id]}
                  </p>
                )}
              </div>

              <div className="mt-8 space-y-10">
                {packGroups.map(({ pack, label, docs }) => (
                  <section key={pack}>
                    <h3 className="font-display text-lg">{label}</h3>
                    <ul className="mt-4 divide-y divide-mist-200 border-y border-mist-200">
                      {docs.map((doc) => (
                        <li
                          key={doc.slug}
                          className="flex flex-wrap items-center justify-between gap-3 py-4"
                        >
                          <div>
                            <p className="font-medium">{doc.title}</p>
                            <p className="text-sm text-ink-700">
                              <code className="text-xs">{doc.slug}.pdf</code>
                              {' · '}
                              {doc.pdfReady ? (
                                doc.stale ? (
                                  <span className="text-amber-800">stale</span>
                                ) : (
                                  'ready'
                                )
                              ) : (
                                <span className="text-red-700">
                                  not generated
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-3 text-sm">
                            <button
                              type="button"
                              disabled={busy || !doc.pdfReady}
                              onClick={() =>
                                downloadPdf(selectedGroup.id, doc.slug)
                              }
                              className="underline disabled:opacity-40"
                            >
                              Download
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() =>
                                regenerate(selectedGroup.id, doc.slug)
                              }
                              className="text-ink-700 underline disabled:opacity-40"
                            >
                              Regenerate
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </Shell>
  );
}
